import { CommonModule } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_ID,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { HookParserEntry, provideDynamicHooks } from 'ngx-dynamic-hooks';
import { AnalyticsService } from 'analytics';
import {
  calcEnvironmentUrls,
  ENVIRONMENT_URLS_CONFIG_TOKEN,
  EnvironmentUrlsConfig,
  translationDynamicValues,
} from 'app-initialization';
import { AuthInterceptor, AuthService } from 'auth-data-access';
import { BRAND_CONFIG, BrandConfig, BrandService } from 'brand';
import { EshopProductService } from 'eshop-product';
import { EshopPromotionService } from 'eshop-promotion';
import { FeatureFlagService } from 'feature-flag';
import { LanguageService } from 'language';
import { LinkComponent } from 'link';
import { CustomErrorHandler, httpErrorInterceptor } from 'monitoring';
import { ChannelService, CmsService, PRISMIC_ENDPOINT } from 'prismic';
import { ProductService } from 'product-shared';
import { PromotionService } from 'promotion-shared';
import { SPRINKLR_APP_ID } from 'sprinklr-webwidget';
import { TooltipComponent } from 'tooltip';
import { TranslateService, TRANSLATION_DYNAMIC_VALUES, TRANSLATION_PRISMIC_TAG } from 'translate';
import { App, BrowserService, FALLBACK_LANGUAGE } from 'utils';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

const firebaseApp = initializeApp(environment.firebaseConfig);

const initFeatureFlags = (featureFlagService: FeatureFlagService, analyticsService: AnalyticsService) => () =>
  featureFlagService.init(environment.featureFlagClientKey, data => analyticsService.addToDataLayer(data));

const environmentUrlsConfig = (browser: BrowserService): EnvironmentUrlsConfig =>
  calcEnvironmentUrls(environment, browser.getOrigin());

const brandConfig: BrandConfig = {
  brand: environment.brand,
};

const componentParsers: Array<HookParserEntry> = [
  { component: TooltipComponent },
  {
    component: LinkComponent,
    inputsWhitelist: ['link'],
  },
];

const translationDynamicValuesConfig = (brandService: BrandService) => translationDynamicValues(brandService.brandName);

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature = withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withComponentInputBinding(), inMemoryScrollingFeature),
    provideClientHydration(withEventReplay()),
    importProvidersFrom(
      BrowserModule,
      FormsModule,
      CommonModule,
      LanguageService,
      CmsService,
      ChannelService,
      AuthService,
      ReactiveFormsModule,
      MatDialogModule
    ),
    provideDynamicHooks(componentParsers),
    CookieService,
    provideAppInitializer(() => {
      const initializerFn = ((cmsService: CmsService, translateService: TranslateService) => async () => {
        await cmsService.init();
        return translateService.initialize();
      })(inject(CmsService), inject(TranslateService));
      return initializerFn();
    }),
    { provide: APP_ID, useValue: App.website },
    { provide: TRANSLATION_DYNAMIC_VALUES, useFactory: translationDynamicValuesConfig, deps: [BrandService] },
    { provide: TRANSLATION_PRISMIC_TAG, useValue: App.website },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { ...new MatDialogConfig(), panelClass: 'modal-container' } },
    { provide: ENVIRONMENT_URLS_CONFIG_TOKEN, useFactory: environmentUrlsConfig, deps: [BrowserService] },
    { provide: BRAND_CONFIG, useValue: brandConfig },
    { provide: FALLBACK_LANGUAGE, useValue: environment.fallbackLang },
    { provide: PRISMIC_ENDPOINT, useValue: environment.prismicEndpoint },
    { provide: ProductService, useClass: EshopProductService },
    { provide: PromotionService, useClass: EshopPromotionService },
    { provide: SPRINKLR_APP_ID, useValue: environment.sprinklerAppId },
    { provide: LOCALE_ID, useValue: 'de-CH' },
    provideAppInitializer(() => {
      const initializerFn = initFeatureFlags(inject(FeatureFlagService), inject(AnalyticsService));
      return initializerFn();
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch(), withInterceptors([httpErrorInterceptor])),
    provideAnimationsAsync(),
    provideFirebaseApp(() => firebaseApp),
    {
      provide: 'FIREBASE_APP',
      useValue: firebaseApp,
    },
  ],
};
